import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import p4lLogo from "../assets/logo/PfL Logo no words 2023-11-21-01.png";
import topicsBackground from "../assets/images/topics_background.svg";
import signInClient from "../auth";
import Button from "../componentLibrary/Button";

import { syncUserRoleAfterLogin } from "../utils/auth";
import { AnalyticsCategoryEnum, SignInStatus } from "../types";
import { analytics } from "../services/AnalyticsService";
import { useGetTutorialStatus } from "../api/queries";
import { useCancelAccountDeletion } from "../api/mutations";
import { getDeviceInfo } from "../utils/deviceDetection";
import { usePermissions } from "../contexts/PermissionContext";
import { useAuth } from "../contexts/AuthContext";
import ConfirmationBox from "../componentLibrary/ConfirmationBox";
import * as client from "../api/client";

const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [deletionDate, setDeletionDate] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetchProfile } = usePermissions();
  const { login: authLogin, isAuthenticated: isAuth } = useAuth();

  const { data: tutorialStatus, isLoading: tutorialLoading } =
    useGetTutorialStatus(isLoggedIn && isAuth);

  // Check if user was redirected due to asset loading error
  const urlParams = new URLSearchParams(location.search);
  const assetError = urlParams.get("error") === "asset_load_failure";
  useEffect(() => {
    if (assetError) {
      // Force clear all cached data
      localStorage.clear();
      sessionStorage.clear();
      // Clear service workers if present
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        });
      }
      // Clear browser caches if supported
      if ("caches" in window) {
        caches.keys().then((names) => {
          names.forEach((name) => {
            caches.delete(name);
          });
        });
      }
      // Clean up the URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [assetError]);

  const cancelAccountDeletion = useCancelAccountDeletion({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountDeletionStatus"],
      });
      setShowRecoveryModal(false);
      setDeletionDate(null);
      // Continue with normal login flow
      setIsLoggedIn(true);
    },
    onError: async () => {
      setLoginError("Failed to recover account. Please try again.");
      setShowRecoveryModal(false);
    },
  });

  const handlePostLoginNavigation = () => {
    if (!tutorialStatus) return;

    const redirectTo = location.state?.from?.pathname || "/explore";
    if (tutorialStatus.needsTutorial) {
      // First-time user needs to see tutorial
      sessionStorage.setItem("showAnnouncementsAfterTutorial", "true");
      navigate("/tutorial");
    } else {
      // Returning user can go to explore
      // Set flag to show announcements after navigation
      sessionStorage.setItem("showLoginAnnouncements", "true");

      // Use setTimeout to ensure sessionStorage is written before navigation
      setTimeout(() => {
        navigate(redirectTo, {
          replace: true,
          state: {
            showAnnouncements: true,
            fromLogin: true,
          },
        });
      }, 100);
    }
  };

  // Handle tutorial status check after login (only if account is not deleted)
  useEffect(() => {
    if (
      isLoggedIn &&
      !tutorialLoading &&
      tutorialStatus &&
      !showRecoveryModal
    ) {
      handlePostLoginNavigation();
    }
  }, [
    isLoggedIn,
    tutorialLoading,
    tutorialStatus,
    showRecoveryModal,
    navigate,
    location.state?.from?.pathname,
  ]);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      const response = await signInClient.post("/sign-in", {
        username,
        password,
      });

      const { status } = response.data;

      if (status === SignInStatus.NEW_PASSWORD_REQUIRED) {
        setIsLoading(false);
        navigate("/set-new-password", {
          state: {
            username,
            password,
          },
        });
      } else if (status === SignInStatus.PENDING_CONFIRMATION) {
        setLoginError("Please check your email for verification link.");
        setIsLoading(false);
      } else {
        // Tokens are now set as HttpOnly cookies by the server
        const userData = response.data.user;
        authLogin({
          sub: userData.sub,
          name: userData.name,
          email: userData.email,
          role: userData.role,
        });

        await syncUserRoleAfterLogin();

        // Reload permissions after login
        await refetchProfile();

        // Track successful login with device information
        const deviceInfo = getDeviceInfo();
        analytics.track("login", {
          category: AnalyticsCategoryEnum.Auth,
          additionalDetails: deviceInfo,
        });

        // Check account deletion status after login
        try {
          const deletionStatusResponse =
            await client.getAccountDeletionStatus();
          if (
            deletionStatusResponse?.isDeleted &&
            deletionStatusResponse?.deletionRequestedAt
          ) {
            const date = new Date(deletionStatusResponse.deletionRequestedAt);
            const formattedDate = date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
            setDeletionDate(formattedDate);
            setShowRecoveryModal(true);
            setIsLoading(false);
            return; // Don't proceed with normal login flow yet
          }
        } catch (error) {
          // If deletion status check fails, continue with normal login
          // eslint-disable-next-line no-console
          console.error("Failed to check account deletion status:", error);
        }

        // Set logged in state to trigger tutorial check
        setIsLoggedIn(true);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;

        if (status === 429) {
          setLoginError(
            data?.message || "Too many login attempts. Please try again later.",
          );
        } else if (data?.status === SignInStatus.UNAUTHORIZED) {
          setLoginError(data?.message || "Invalid username or password.");
        } else {
          setLoginError("An error occurred during login.");
        }
      } else {
        setLoginError("An error occurred during login.");
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative pb-6 bg-gradient-to-b from-electrum-100 via-white to-white">
      {/* Fixed Background Image */}
      <div
        className="fixed inset-0 z-0 top-8"
        style={{
          backgroundImage: `url(${topicsBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      />
      <div className="relative">
        <div className="flex flex-col mx-auto w-full max-w-96 px-4 mt-16 space-y-6">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-8">
            <div className="flex flex-col items-center mb-8">
              <img
                src={p4lLogo}
                alt="Psych For Life logo"
                className="w-32 h-32 mb-2"
              />
              <p className="text-gray-900 text-3xl">
                Psych For Life<sup>®</sup>
              </p>
            </div>
            <div className="space-y-6">
              <p className="text-gray-900 text-2xl font-bold">Log in</p>
              {assetError && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                  <div className="flex items-start">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-orange-700">
                        We&apos;ve updated the app.
                      </h3>
                      <p className="text-sm text-orange-700 mt-1">
                        Log in again to view the new features.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                type="username"
                name="username"
                value={username}
                onChange={(e) => {
                  setLoginError("");
                  setUsername(e.target.value);
                }}
              />
              <TextField
                label="Password"
                variant="outlined"
                fullWidth
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => {
                  setLoginError("");
                  setPassword(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLogin();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                className="w-full py-4 text-lg"
                onClick={handleLogin}
                loading={isLoading}
              >
                Log in
              </Button>
              {loginError && <p className="text-red-500">{loginError}</p>}
              <div className="text-center space-y-2">
                <div className="flex flex-col space-y-1">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-500 hover:text-blue-600"
                  >
                    Forgot Password?
                  </Link>
                  <Link
                    to="/forgot-username"
                    className="text-sm text-blue-500 hover:text-blue-600"
                  >
                    Forgot Username?
                  </Link>
                </div>
                <p className="text-sm text-gray-600">
                  Have a code?{" "}
                  <Link
                    to="/signup"
                    className="text-blue-500 hover:text-blue-600"
                  >
                    Sign up
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationBox
        isOpen={showRecoveryModal}
        title={
          deletionDate
            ? `This account was marked for deletion on ${deletionDate}`
            : "This account was deleted"
        }
        message={
          <div>
            <p className="mb-2">
              Would you like to recover your account and log in? If you recover
              it, your account will be restored and you can continue using it
              normally.
            </p>
          </div>
        }
        confirmText="Recover Account"
        cancelText="Cancel"
        onConfirm={() => cancelAccountDeletion.mutate()}
        onCancel={async () => {
          setShowRecoveryModal(false);
          await client.logout();
          setLoginError(
            "Account recovery cancelled. Please contact support if you need assistance.",
          );
        }}
      />
    </div>
  );
};

export default LoginPage;
